import useViewedProducts from "@/hooks/useViewedProducts";
import useWishlistStore from "@/hooks/useWishlistStore";
import { formatNumber } from "@/lib/utils";
import {
  IoCameraSharp,
  IoHeart,
  IoHeartOutline,
  IoStarSharp,
  IoTrashOutline,
} from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import tagBadge from "../../assets/tag.png";
import AddToCartButton from "./add-to-cart-button";

import { Helmet } from "react-helmet";
import { useMediaQuery } from "react-responsive";
import { toast } from "sonner";
import LazyImage from "./lazy-image";

type Product = {
  openInNewTab?: boolean;
  sku: number;
  title: string;
  subtitle?: string;
  brand?: string;
  rating: number;
  reviews: number;
  is_add_to_cart_available: boolean;
  currency: string;
  selling_price: number;
  listing_price: number;
  surcharge?: number;
  markup: number;
  stock_available: boolean;
  delivery_days: number;
  next_delivery_date: string;
  original?: number;
  image: {
    thumbnail: string;
    count: number;
  };
  badges: any[];
  quantity?: number | null;
  variants?: any[];
};

export default function ProductCard(
  props: Product & { horizontal?: boolean; removableFromWishlist?: boolean }
) {
  const navigate = useNavigate();

  const isHorizontal = props.horizontal;
  const removableFromWishlist = props.removableFromWishlist;

  const {
    openInNewTab,
    sku,
    title,
    subtitle,
    brand,
    rating,
    reviews,
    image,
    delivery_days,
    next_delivery_date,
    currency,
    selling_price = 0,
    listing_price = 0,
    markup,
    is_add_to_cart_available,
    stock_available,
    badges,
    quantity,
    original,
  } = props;

  const { addToWishlist, isInWishlist, removeFromWishlist } =
    useWishlistStore();

  const handleWishlistClick = (id: number) => {
    if (isInWishlist(id)) {
      removeFromWishlist(id);
      toast.error("Item removed from Wish List");
    } else {
      addToWishlist(id);
      toast.success("Item added to Wish List");
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const { addViewedProduct } = useViewedProducts();

  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <Link
      // className="cursor-pointer max-w-64 h-full bg-white rounded-none sm:rounded-lg duration-500 flex flex-col justify-between border border-[#E8E8E8] lg:hover:border-primary"
      className={`flex relative cursor-pointer h-full bg-white duration-200 border border-[#E8E8E8] rounded-none sm:rounded-lg lg:hover:border-primary
				${
          isHorizontal
            ? " flex-row max-w-none items-center gap-4 lg:p-2"
            : "max-w-64 flex-col justify-between"
        }
			`}
      to={`/products/PLID${sku}`}
      onClick={(e) => {
        // Modifier keys are present (Ctrl/Cmd click, etc.)
        // or if it's a right click (which doesn't trigger onClick, but kept for completeness)
        const isModifierKeyPressed = e.metaKey || e.ctrlKey || e.button === 2;

        if (openInNewTab && !isModifierKeyPressed) {
          e.preventDefault();
          window.open(`/products/PLID${sku}`, "_blank", "noopener noreferrer");
        } else if (!isModifierKeyPressed) {
          e.preventDefault();

          addViewedProduct({
            sku,
            title,
            rating,
            reviews,
            image,
            delivery_days,
            next_delivery_date,
            currency,
            selling_price,
            listing_price,
            markup,
            is_add_to_cart_available,
            stock_available,
            badges,
            quantity,
          });
          navigate(`/products/PLID${sku}`);
          scrollToTop();
        }
        // If a modifier key is pressed, the default <Link> behavior takes over, supporting new tabs, windows, etc.
      }}
    >
      <Helmet>
        <meta property="og:site_name" content="The Daily Sale Shop" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={title ?? "Find great deals on our products"}
        />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={image.thumbnail.replace(
            "s-fb.file",
            "s-pdpxl.file"
          )}
        />
        <meta
          property="product:availability"
          content={
          "in stock"
          }
        />
        <meta property="product:id" content={`PLID${sku}`} />
        <meta
          property="product:retailer_item_id"
          content={`PLID${sku}`}
        />
        <meta
          property="product:price:amount"
          content={formatNumber(selling_price)}
        />
        <meta
          property="product:availability"
          content={"in stock"}
        />
        <meta
          property="product:price:currency"
          content={'USD'}
        />
      
      </Helmet>

      <div
        className={`${isHorizontal ? "w-1/3 sm:w-48 overflow-hidden" : ""} `}
      >
        <div className="flex justify-center hover:bg-red-100">
          <LazyImage
            lowQualitySrc={image.thumbnail}
            highQualitySrc={image.thumbnail.replace(
              "s-fb.file",
              "s-pdpxl.file"
            )}
            alt="No Image"
            className={`rounded-t-xl w-full ${isHorizontal ? "p-2" : "p-8 -mt-6"} object-cover `}
          />

          <div className="flex flex-col absolute top-3 left-2 gap-2">
            {next_delivery_date == "Tomorrow" && (
              <div className=" bg-green-400 p-2 rounded-sm text-red-500 font-bold uppercase text-[10px] w-8 h-8 flex justify-center text-center items-center">
                🔥 HOT
              </div>
            )}
            {badges &&
              badges.length > 0 &&
              badges.map((badge, key) =>
                badge.type === "saving" ? (
                  <div
                    key={key}
                    className=" bg-primary p-2 rounded-sm text-white font-bold uppercase text-[10px] w-8 h-8 flex justify-center text-center items-center"
                  >
                    {String(badge.value).replace("Up to", "")}
                  </div>
                ) : (
                  <div key={key} className="">
                    <img
                      className="object-fit bg-primary p-1 rounded-sm text-[10px] w-8 h-8 flex justify-center items-center shadow-md hover:shadow-lg transform hover:scale-110 transition duration-300"
                      src={tagBadge}
                    />
                  </div>
                )
              )}
            {quantity && (
              <div className=" bg-primary p-2 rounded-sm text-white font-bold uppercase text-[10px] w-8 h-8 flex justify-center text-center items-center">
                {`${quantity} LEFT`}
              </div>
            )}
          </div>
          {!isHorizontal && (
            <div
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                handleWishlistClick(sku);
              }}
              className="absolute top-3 right-2 hover:bg-gray-200 duration-200 p-2 rounded-full group"
            >
              {isInWishlist(sku) ? (
                <IoHeart className="group-hover:text-red-500 duration-200" />
              ) : (
                <IoHeartOutline className="group-hover:text-red-500 duration-200" />
              )}
            </div>
          )}
        </div>

        {!isHorizontal && (
          <div
            className={`flex justify-between items-center px-4 -mt-6 ${isHorizontal ? "mb-4" : ""}`}
          >
            <div className=" bg-gray-300 rounded-sm py-1 px-2 hidden sm:flex justify-center items-center gap-2 text-xs">
              <IoCameraSharp size={15} /> {image.count}
            </div>
            {reviews > 0 && (
              <span className=" text-[#707072] flex flex-row text-[8px] sm:text-xs justify-start items-center gap-2">
                <IoStarSharp size={14} color="#fbb600" />
                {rating} ({formatNumber(reviews, false)} Reviews)
              </span>
            )}
          </div>
        )}
      </div>

      <div
        className={`w-full flex h-full ${isHorizontal ? "sm: flex-col lg:flex-row" : "flex-col"}`}
      >
        <div
          className={`${isHorizontal ? "flex flex-1 flex-col justify-between items-start py-3 px-2" : "px-4 py-3 flex-1"}`}
        >
          <div className={`${isHorizontal ? "flex flex-col-reverse" : ""}`}>
            {isHorizontal && (
              <span className="text-blue-400 hidden sm:block text-sm">
                {brand}
              </span>
            )}
            {isHorizontal && (
              <span className="text-gray-400 hidden sm:block text-sm">
                {subtitle}
              </span>
            )}
            {is_add_to_cart_available ? (
              <div>
                {stock_available ? (
                  <span className="text-red-500 mr-3 uppercase text-[8px] sm:text-[10px]">
                    {next_delivery_date
                      ? `Receive Between ${next_delivery_date}`
                      : "Continue Shopping"}
                  </span>
                ) : (
                  <span className="text-red-500 mr-3 uppercase text-xs">
                    Out of Stock
                  </span>
                )}
              </div>
            ) : (
              <span className="text-red-500 mr-3 uppercase text-[8px] sm:text-[10px]">
                {next_delivery_date
                  ? `Receive Between ${next_delivery_date}`
                  : "Continue Shopping"}
              </span>
            )}
            <p
              className={`${isHorizontal ? "text-md" : "text-xs"} text-black line-clamp-2`}
            >
              {title}
            </p>
          </div>

          {isHorizontal && (
            <div className={`flex w-full justify-between items-center my-1`}>
              <div className=" bg-gray-300 rounded-sm py-1 px-2 flex justify-center items-center gap-2 text-xs">
                <IoCameraSharp size={15} /> {image.count}
              </div>
              {reviews > 0 && (
                <span className=" text-[#707072] flex flex-row text-[10px] sm:text-xs justify-start items-center gap-2">
                  <IoStarSharp size={14} color="#fbb600" />
                  {rating} ({formatNumber(reviews, false)} Reviews)
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className={`${isHorizontal ? "w-full lg:w-1/4  py-3 pt-0 flex flex-col justify-between p-2" : ""}`}
        >
          <div
            className={`flex ${isHorizontal ? "flex-row justify-between lg:justify-normal lg:flex-col items-center lg:items-end" : "flex-row items-center justify-between px-4"}   pb-2`}
          >
            <p
              className={`text-xs text-center   ${isHorizontal ? "sm:text-2xl" : "sm:text-sm"} font-semibold text-[#CD0F0F] `}
            >
              {currency} {formatNumber(selling_price)}
            </p>
            {listing_price !== 0 && (
              <del className="text-gray-400">
                <p className="text-xs sm:text-sm text-gray-400 ml-2">
                  {currency} {formatNumber(listing_price)}
                </p>
              </del>
            )}
          </div>
          <div className="flex flex-row lg:flex-col gap-2">
            <div
              className={`w-full ${isHorizontal ? "" : "p-2"}`}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            >
              <AddToCartButton
                openInNewTab={openInNewTab}
                title={title}
                image={image.thumbnail}
                is_add_to_cart_available={is_add_to_cart_available}
                sku={sku}
                currency={currency}
                delivery_days={delivery_days}
                next_delivery_date={next_delivery_date}
                price={selling_price}
                markup={markup}
                original_zar_price={original}
                stock_available={stock_available}
              />
            </div>
            {isHorizontal && (
              <div
                className={`flex group w-full ${removableFromWishlist ? "bg-red-500" : "bg-gray-300"} hover:bg-black  text-[10px] sm:text-sm justify-center items-center gap-2 p-2 lg:p-2.5 rounded-sm`}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  handleWishlistClick(sku);
                }}
              >
                {removableFromWishlist ? (
                  <IoTrashOutline className="text-white" />
                ) : (
                  <div>
                    {isInWishlist(sku) ? (
                      <IoHeart className="group-hover:text-red-500 duration-200 text-gray-600" />
                    ) : (
                      <IoHeartOutline className="group-hover:text-red-500 duration-200 text-gray-600" />
                    )}
                  </div>
                )}
                {removableFromWishlist ? (
                  <span className="text-white group-hover:text-white">
                    {isMobile ? "Remove" : "Remove from Wish List"}
                  </span>
                ) : (
                  <span className="text-gray-600 group-hover:text-white">
                    {isInWishlist(sku) ? "Added" : "Add"} to Wish List
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
